import { useState, useEffect } from 'react';
import { UsersApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { formatDate, handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import DataGrid, { Column, Pager, Paging } from 'devextreme-react/data-grid';
import { GridSettingType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf, faFileExcel, faArrowCircleDown } from '@fortawesome/free-solid-svg-icons';

const UserAgreementsTable = ({ id }) => {
    const usersApi = new UsersApi.UsersApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = allowedPageSizes[1];

    const [data, set_data] = useState(null);

    const getDataAsync = async (id) => {
        if (!id || id === 0) {
            return undefined;
        }

        try {
            const response = await usersApi.getUserAgreements(id);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve data!');
            }
            const records = response.result.data;
            set_data(records);
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve user agreements! Please refresh the page and try again.');
        }
    };

    const refreshGrid = (event) => {
        event.preventDefault();
        refreshData();
    };

    const refreshData = async () => {
        await getDataAsync(id);
    };

    useEffect(() => {
        refreshData();
    }, [id]);

    const downloadAgreementPDF = async (event, agreementDownloadToken) => {
        event.preventDefault();

        const response = await fileApi.downloadUrl({
            token: agreementDownloadToken,
        });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const createAgreementPDF = async (event, id) => {
        try {
            event.preventDefault();
            const response = await usersApi.createUserAgreementPdf(id);

            if (response?.statusCode === 200) {
                handleAdminSuccess('The system was able to proceed with PDF generation.  It should be available shortly.');
                refreshGrid(event);
            } else {
                handleAdminError('The system was not able to process this PDF. Please contact support.');
            }
        } catch {
            handleAdminError('The system was not able to process this PDF. Please contact support.');
        } finally {
        }
    };

    const dateCellRender = (date) => {
        if (date) {
            return <>{formatDate(date)}</>;
        }

        return;
    };

    const fileCellRender = (rowData) => {
        if (!rowData) {
            return '';
        }

        if (rowData.signedUserAgreementGeneratingUrl) {
            return (
                <span
                    style={{
                        color: 'black',
                        width: '90px',
                        textAlign: 'center',
                        display: 'inline-block',
                    }}
                    onClick={(e) => refreshGrid(e)}
                >
                    <FontAwesomeIcon
                        title='User Agreement PDF is generating...click to refresh status.'
                        icon={faHourglassHalf}
                    />
                </span>
            );
        } else if (rowData.signedUserAgreementDownloadToken) {
            return (
                <button
                    className='gridButton'
                    onClick={(e) => downloadAgreementPDF(e, rowData.signedUserAgreementDownloadToken)}
                >
                    <FontAwesomeIcon icon={faArrowCircleDown} /> Download
                </button>
            );
        } else {
            return (
                <button
                    className='gridButton red-text'
                    onClick={(e) => createAgreementPDF(e, rowData.id)}
                >
                    <FontAwesomeIcon icon={faFileExcel} /> Create PDF
                </button>
            );
        }
    };

    const versionCellRender = (versionName) => {
        if (versionName) {
            return <>{versionName}</>;
        }

        return;
    };

    return (
        <>
            <div style={{ padding: '15px' }}>
                <DataGrid
                    gridType={GridSettingType.UserAgreementsTable}
                    dataSource={data}
                    showBorders={true}
                    showRowLines={false}
                    rowAlternationEnabled={true}
                    applicationInsights={applicationInsights}
                    remoteOperations={false}
                    noDataText='No User Agreements available'
                >
                    <Column
                        dataField='userAgreementType'
                        caption='Agreement Type'
                        dataType='string'
                    />
                    <Column
                        dataField='dateSigned'
                        caption='Date Signed'
                        cellRender={(e) => dateCellRender(e.data.dateSigned)}
                        width={120}
                    />
                    <Column
                        caption='Version'
                        dataType='string'
                        cellRender={(e) => versionCellRender(e.data.templateVersion.name)}
                    />
                    <Column
                        dataField='fileLink'
                        caption='User Agreement File'
                        cellRender={(e) => fileCellRender(e.data)}
                    />
                    <Paging defaultPageSize={defaultPageSize} />
                    <Pager
                        displayMode='full'
                        showPageSizeSelector={allowedPageSizes.length > 1}
                        allowedPageSizes={allowedPageSizes}
                        showNavigationButtons={true}
                        showInfo={true}
                        visible={true}
                        location='before'
                    />
                </DataGrid>
            </div>
        </>
    );
};

export default UserAgreementsTable;
